import * as rhythm from "../features/rhythm/rhythm.js";
import * as time from "../utils/time.js";
import { setFrontMatterConfigOnWindow } from "./frontmatter.js";
import { logsReady } from "./obtain-log-file.js";
import {
  calculateEntrySummary,
  calculateEntryEnd,
  calculateEntryDuration,
  parseEntryHabits,
} from "./entries/entries.js";
import { buildLogObjFromSourceLogFile } from "./log-source.js";
import { updateDataGoals } from "../features/goals/goals.js";

/**
  What's in this file really?

  - Code to build the basic log. Is that really calling all these other functions? Can we simplify it?
  - Promise to initialise log building and return final log.
  - Main classes: TCHA, Entry, Goal, etc.
  - Misc utilities that can probably go into something like KTT Utils

  Code to create
   - Goals
   - Entries
   - Summaries
  */

let databasePromise = null;

const initialiseDatabase = async () => {
  if (databasePromise) return databasePromise;

  databasePromise = (async () => {
    const result = await logsReady();

    if (!result) {
      throw new Error("Logs not ready");
    }

    setFrontMatterConfigOnWindow(result.configFile);

    const logData = entrypointToBuildEntireLog(result);

    return logData;
  })();

  try {
    const data = await databasePromise;
    return data;
  } catch (error) {
    console.warn("KTT initialiseDatabase() failed");
    databasePromise = null;
    throw error;
  }
};

export const getDatabase = async () => {
  return databasePromise || initialiseDatabase();
};

window.getDatabase = async () => getDatabase();

// Making the Logs ======================================================

/**
  Function handles all stages of building up the initial database file.
  - Making a log object out of the initial text log
  - calculating the data within the database:
    - setting up the front-matter object from the config (areas, projects)
    -
*/
async function entrypointToBuildEntireLog(result) {
  /**
  - one option here is to keep returning the log to a constant variable, then returning the entire thing, and then awaiting that variable for the rest of the program to use.
  */

  let logObj = buildInternalLogObject(result.logSource);

  window.logObj = logObj;

  // stuff that doesnt depend on Period view
  logObj = calculateDataForEachEntryInTheLog(logObj);

  console.log("🗒️ window.fm: ", window.fm);
  console.log("🎯 window.fm.goals: ", window.fm.goals);
  console.log("🗄 Log: ", logObj);

  // calculate extra data that depends on Period view?
  calculateDataAndStats(logObj);

  return logObj;
}

// Create the Master Log in JSON
// ======================================================
function buildInternalLogObject(logSource) {
  // todo: receive just the logSource, not all the files
  const logObj = buildLogObjFromSourceLogFile(logSource);

  // make asc and desc versions of the log
  logObj.fullLogDesc = Object.entries(logObj.fullLog).sort((date1, date2) => {
    return time.dateNoTime(date1[0]) > time.dateNoTime(date2[0]);
  });

  logObj.fullLogAsc = [...logObj.fullLogDesc].reverse();

  // make no_time version of the logObj. all times on Dates set to 00:00
  logObj.fullLogNoTime = {};
  Object.values(logObj.fullLogAsc).forEach((each) => {
    logObj.fullLogNoTime[time.dateNoTime(each[0])] = each[1];
  });

  return logObj;
}

function calculateDataForEachEntryInTheLog(logObj) {
  // Entry endings and durations
  // Entry habit data
  // Summary of each day
  // Rhythm Data for Moods, Energy, Clarity
  // Cycles data for Wake, Rise, Sleep

  // Stuff for each Day
  // ==================
  for (let x = 0; x < logObj.fullLogAsc.length; x++) {
    const date = time.dateNoTime(logObj.fullLogAsc[x][0]);
    const eachDay = logObj.fullLogAsc[x][1];

    eachDay.date = time.getDateStrings(date);
    window.ui.keys.forEach((key) => (eachDay[key] = {}));

    // Stuff for each Entry:
    // ==================
    for (let a = 0; a < eachDay.entries.length; a++) {
      let entry = eachDay.entries[a];
      // console.log(entry)

      // calculate entry end-times and entry durations
      let nextEntry = eachDay.entries[a + 1];
      calculateEntryEnd(entry, nextEntry, x, logObj);
      calculateEntryDuration(entry, eachDay);

      // Entry habit data
      if (entry.comment && entry.comment.match(window.regExes.JSON)) {
        parseEntryHabits(entry, eachDay, date);
      }
    }

    // Stuff for each Day:
    // ==================

    // calculate summary data for each day
    let summary = calculateEntrySummary(eachDay.entries);
    calculateEntrySummaryDurationTotal(summary);
    eachDay.summary = summary;

    // Make Habit summary
    // eachDay.habits

    // add in rhythm data for each day
    eachDay.mood = rhythm.getRhythmDataForDay(eachDay, window.fm.mood);
    eachDay.energy = rhythm.getRhythmDataForDay(eachDay, window.fm.energy);
    eachDay.clarity = rhythm.getRhythmDataForDay(eachDay, window.fm.clarity);

    // See if any entries reach flow level
    eachDay.rhythmFlow = {};
    eachDay.rhythmFlow.mood = getRhythmFlowDataForDay(eachDay.mood);
    eachDay.rhythmFlow.energy = getRhythmFlowDataForDay(eachDay.energy);
    eachDay.rhythmFlow.clarity = getRhythmFlowDataForDay(eachDay.clarity);

    // Add cycles data for each day
    // wake, rise, meal times, sleep

    eachDay.cycles = getCyclesDataForDay(eachDay);
    const { slept, inBed } = getTimeSlept(date, logObj);
    eachDay.slept = slept;
    eachDay.inBed = inBed;
  }

  return logObj;
}

function calculateDataAndStats(logObj) {
  // Update Various Log Data
  // ======================================================
  updateDataTags();
  updateDataGoals();
  // parseGoals()

  window.fm.cycles = [].concat(window.fm.categories.cycles);
  window.fm.mood = [].concat(window.fm.categories.mood);

  delete window.fm.categories.cycles;
  delete window.fm.categories.mood;
  delete window.fm.categories.energy;
  delete window.fm.categories.clarity;

  // loop through logObj.Years for each week's days, and put data into a week.summary
  // loop through logObj.Years for each week's summary, and put data into a month.summary

  // Object.keys(logObj.Years).forEach( year => {
  //   Object.values(year).forEach( month => {
  //     Object.entries(month).forEach ( week => {
  //     })
  //   })
  // })

  for (let [keyY, valueY] of Object.entries(logObj.Years)) {
    // console.log(keyY)
    // console.log(valueY)
    for (let [keyM, valueM] of Object.entries(valueY)) {
      if (keyM != summary) {
        // valueM.summary = aggregateDailySummariesIntoWeeklyMonthly(valueM)

        for (let [keyW, valueW] of Object.entries(valueM)) {
          if (keyW != summary) {
            if (Object.keys(valueW)) {
              // All of the entries of the week, grouped into a single object
              let summaryData = makeHabitSummaryData(valueW);
              // Merge the object child keys properly
              valueW.summary = aggregateDailySummariesIntoWeeklyMonthly(summaryData);
            }
            // console.log(valueW.summary)
            // all the summary data can be made here actually, not just the habit summaries
          }
        }
        // console.info(valueM)
        // If date_range is a month object with nested weeks,
        // we flatten it into an Object of days instead
        // (month objects don't have 'summary' children in them yet)''
        if (Object.keys(valueM)) {
          let month_days = getChildObjs(valueM, 1);
          let summaryData = makeHabitSummaryData(month_days);
          valueM.summary = aggregateDailySummariesIntoWeeklyMonthly(summaryData);
        }
        // console.log(valueM.summary)
      }
    }
  }

  // console.log(logObj.Years)
  // calculate all frontmatter lists, category and area summaries, moods stats
  // calculate averages, totals, etc
  // delete window.fm.categories.misc
}

// Misc
// ======================================================
function calculateEntrySummaryDurationTotal(summary) {
  // count the durations and put the total to the start of each summary entry
  let summaryEntries = Object.keys(summary);

  for (let x = 0; x < summaryEntries.length; x++) {
    let total = 0;
    for (let y = 0; y < summary[summaryEntries[x]].length; y++) {
      if (typeof summary[summaryEntries[x]][y] == "number") total += summary[summaryEntries[x]][y];
    }
    summary[summaryEntries[x]] = total; //.unshift(`total ${total}`) (include each duration time)
  }
}

function getRhythmFlowDataForDay(rhythmObj) {
  // return true if there is a flow entry here, false if not
  let flowEntries = Object.values(rhythmObj).filter((val) => val >= window.config.settings.flow);
  return flowEntries.length > 0;
}

function getCyclesDataForDay(date) {
  let cycles = buildCyclesObj();

  Object.values(date.entries).forEach((entry) => {
    Object.keys(cycles).forEach((key) => {
      if (entry.tags.includes(key)) {
        cycles[key] = entry.start;
      }
    });
  });
  return cycles;
}

function buildCyclesObj() {
  let cycles = {};
  window.fm.cycles.forEach((item) => (cycles[item] = ""));
  return cycles;
}

function getTimeSlept(date, logObj) {
  const timeSlept = { slept: undefined, inBed: undefined };

  if (date instanceof Date == false) {
    console.warn("Invalid date: ", date);
    return timeSlept;
  }

  const yesterday = getLogObjFromDate(time.getYesterday(date), logObj);

  // we add 24h to make the comparisons the same
  // ie. bed at 23, wake at 8.
  // 8+24 = 32, so 23-32 is the hours slept.
  const wakeTime = add24H(getLogObjFromDate(date, logObj).cycles.wake) || 0;
  const riseTime = add24H(getLogObjFromDate(date, logObj).cycles.rise) || 0;
  let sleepTime = yesterday?.cycles ? yesterday.cycles.sleep : 0;

  // if sleepTime is midnight or later, also add 24.
  if (sleepTime && parseInt(sleepTime.match(/\d\d/)[0]) < 12) {
    sleepTime = add24H(sleepTime);
  }

  // console.log(`sleepTime: ${sleepTime}`)
  // console.log(`wakeTime: ${wakeTime}`)
  // console.log(`riseTime: ${riseTime}`)

  if (wakeTime && sleepTime) {
    // calculate the difference
    timeSlept.slept = time.minsToHM(time.calculateMinutesBetweenTwoTimes(sleepTime, wakeTime)) || "?";
    timeSlept.inBed = time.minsToHM(time.calculateMinutesBetweenTwoTimes(sleepTime, riseTime)) || "?";
  }

  return timeSlept;
}

function getLogObjFromDate(date, logObj) {
  return logObj.fullLogNoTime[time.dateNoTime(date)];
}

function add24H(time) {
  if (!time) {
    return;
  }
  // receives time string in 00:00 format

  time = time.match(/\d\d/g);
  let timeHH = parseInt(time[0]) + 24;
  let timeMM = time[1];
  return `${timeHH}:${timeMM}`;
}

// TAG Objs in FrontMatter
function updateDataTags() {
  Object.values(window.fm.tags).forEach((tag) => {
    tag.update_tag_data();
  });
}

function makeHabitSummaryData(date_range, summaryData = {}) {
  for (let eachDay of Object.values(date_range)) {
    if (eachDay.habits) {
      // grab all the habit summary data
      for (let [tag, obj] of Object.entries(eachDay.habits)) {
        if (obj.summary) {
          // make a tag entry for this summary if it doesn't exist yet
          if (!summaryData[tag]) {
            summaryData[tag] = [];
          }
          // add summary data object to
          summaryData[tag].push(obj.summary);
        }
      }
    }
  }
  // if (Object.keys(summaryData).length > 0) { console.log(summaryData) }
  return summaryData;
}

function getChildObjs(object, levels_deep) {
  // given an object
  // get all the objects's children and add them to a new empty object
  // for each of those, call this function again levels_deep number of times
  // make it drill down until it reaches keys that are a date.
  // so i can use it for Years, Quaters, Months

  let flattenedObj = {};

  Object.entries(object).forEach((child) => {
    Object.entries(child).forEach((subChild) => {
      Object.entries(subChild).forEach((x) => {
        if (typeof x[1] != "string") {
          Object.entries(x[1]).forEach((y) => {
            flattenedObj[y[0]] = y[1];
          });
        }
      });
    });
  });
  // console.log(flattenedObj)
  delete flattenedObj.summary;

  return flattenedObj;
}

function aggregateDailySummariesIntoWeeklyMonthly(summaryData = {}) {
  if (Object.keys(summaryData).length === 0) {
    return "";
  }

  // console.log('summaryData:')
  // console.log(summaryData)

  for (let key in summaryData) {
    // console.log('summaryData[key]')
    // console.log(summaryData[key])
    if (key != "") {
      summaryData[key] = mergeObjects(summaryData[key]);
    }
  }
  // console.log(summaryData)
  return summaryData;
}

function mergeObjects(arr) {
  let mergedObj = {};

  for (let i = 0; i < arr.length; i++) {
    let obj = arr[i];
    for (let key in obj) {
      if (typeof obj[key] === "object") {
        mergedObj[key] = mergeObjects([mergedObj[key], obj[key]]);
      } else if (typeof obj[key] === "number") {
        mergedObj[key] = mergedObj[key] ? mergedObj[key] + obj[key] : obj[key];
      } else {
        mergedObj[key] = obj[key];
      }
    }
  }

  return mergedObj;
}
